// DMA - v0.2 Types (Hybrid Model Refinement)

export interface Sector {
  id: string;
  name: string;
}

export enum FieldType {
  TEXT_SHORT = 'text_short',
  TEXT_LONG = 'text_long',
  NUMBER = 'number',
  DATE = 'date',
  SELECT = 'select',
  RADIO = 'radio',
  CHECKBOX = 'checkbox',
  YES_NO = 'yes_no',
  RATING = 'rating',
  LINEAR_SCALE = 'linear_scale',
  MATRIX = 'matrix',
  MEDIA_PHOTO = 'media_photo',
  SIGNATURE = 'signature',
}

export type CalculationOperation =
  | "EQUALS"
  | "NOT_EQUALS"
  | "CONTAINS"
  | "GREATER_THAN"
  | "LESS_THAN"
  | "GREATER_THAN_EQUALS"
  | "LESS_THAN_EQUALS"
  | "AND"
  | "OR";

export enum SignatureMethod {
  HANDWRITTEN = 'handwritten',
  CREDENTIAL = 'credential',
  BIOMETRIC_VISUAL = 'biometric_visual',
}

export interface FieldOption {
  label: string;
  value: string;
}

export interface MatrixColumn {
  id: string;
  label: string;
  type: 'text' | 'number' | 'select';
  options?: string[]; // Comma separated values for select type in matrix
}

export interface FieldSchema {
  id: string;
  type: FieldType;
  label: string;
  required: boolean;
  options?: FieldOption[]; // Used for Select, Radio, Checkbox
  signatureConfig?: {
    method: SignatureMethod;
    allowRefusal: boolean;
  };
  mediaConfig?: {
    allowMultiple: boolean;
    allowVideo: boolean;
  };
  ratingConfig?: {
    maxStars: number;
  };
  linearScaleConfig?: {
    min: number;
    max: number;
    minLabel: string;
    maxLabel: string;
  };
  matrixConfig?: {
    columns: MatrixColumn[];
    preventDuplicatesOnFirstColumn?: boolean;
  };
}

// "Versão do Formulário" (Snapshot - Immutable Schema)
export interface FormVersion {
  version: number;
  schema: FieldSchema[]; // JSONB content in DB
  createdAt: string;
  createdBy: string;
}

// "Formulário Pai" (Container - Relational Metadata)
export interface FormMetadata {
  id: string;
  tenantId: string; // Multitenancy support
  title: string;
  description: string;
  sectorId: string; // FK to sectors
  sector: string; // Display Name (Denormalized/Joined)
  currentVersion: number;
  requireLocation: boolean;
  permitir_edicao?: boolean;
  versions: FormVersion[];
  status: 'active' | 'archived';
  createdAt: string;
  updatedAt: string;
}

export interface FormResponseValue {
  value: any; // Changed to any to support array of strings for media/checkbox/matrix
  metadata?: {
    timestamp: string;
    geolocation?: { lat: number; lng: number };
    refusalReason?: string;
  };
}

// "Sessão de Resposta"
export interface FormResponse {
  id: string;
  tenantId: string;
  formId: string;
  formVersion: number; // Linked to specific Snapshot
  respondentId: string;
  respondentName: string;
  data: Record<string, FormResponseValue>; // "Valores da Resposta" (JSONB Payload)
  startedAt: string; // Session start
  submittedAt: string; // Session end
  status: 'draft' | 'submitted' | 'rejected' | 'pending';
  deviceInfo?: {
    ip?: string;
    userAgent?: string;
    geolocation?: { lat: number; lng: number };
  };
  securityMetadata?: {
    hash: string;
    algorithm: string;
    timestamp: string;
  };
}

export interface User {
  id: string;
  tenantId: string;
  name: string;
  role: 'admin' | 'operator';
  sectorIds: string[]; // FKs to sectors
  sector?: string; // Optional for backwards compatibility
  sectors?: Sector[]; // Fully populated sectors
  email?: string;
  needsPasswordReset?: boolean;
  avatarUrl?: string;
  status: 'active' | 'inactive';
}


/* =========================================================
   INSIGHTS VIEW TYPES
   ========================================================= */
export type KPICalcType = 'sum' | 'avg' | 'count' | 'count_unique' | 'delta' | 'max' | 'min' | 'mode' | 'median';
export type AggregationType = 'SUM' | 'AVG' | 'COUNT' | 'COUNT_UNIQUE' | 'MIN' | 'MAX' | 'NONE' | 'LATEST';
export type ChartType = 'kpi' | 'pie' | 'bar' | 'line' | 'area' | 'area3d' | 'scatter' | 'composed' | 'table';
export type Mode = 'map' | 'analytics';

export interface CardConfig {
  id: string;
  title: string;
  viz: ChartType;
  tab?: string; // Aba do card (ex: "Controle Diário de Produção")
  // for KPI:
  fieldId?: string;
  kpiMode?: AggregationType;
  calcType?: KPICalcType;
  // for charts:
  xAxisFieldId?: string;
  yAxisFieldId?: string;
  zAxisFieldId?: string;
  groupAxisFieldId?: string;
  seriesAxisFieldId?: string; // used to pivot lines/bars into multiple series

  // Aggregation & Rules
  aggregationType?: AggregationType;
  isAggregated?: boolean; // Legacy

  numberFormat?: 'decimal' | 'time'; // Formatação opcional de números decimais

  dateGrouping?: 'day' | 'week' | 'month' | 'bimonth' | 'semester' | 'year';
  // Authorship (immutable signature — analytics mode only)
  createdBy?: string;      // user display name
  createdById?: string;    // user id (stable)
  width?: 'half' | 'medium' | 'full';
  height?: 'normal' | 'tall';
  pivotRowTotals?: boolean;
  pivotColTotals?: boolean;
}
